import * as React from 'react';
import { View, Modal, TouchableOpacity, ScrollView } from 'react-native';
import { Text, Icon, Button } from 'react-native-elements';
import { checkProductsStock } from 'services/productService';
import { storeFoodTruckOrder } from 'services/ordersService';
import OrderContext from 'contexts/OrderContext';
import OrderDetailsModalProps from 'types/components/OrderDetailModalProps';
import ShoppingCart from 'types/models/ShoppingCart';
import useMediumScreen from 'hooks/useMediumScreen';
import useNotProcessedOrders from 'hooks/useNotProcessedOrders';
import { useFormatMoneyAuto } from 'hooks/useFormatMoneyAuto';
import { calculateTotalAmount } from 'utils/orders';
import { styles } from 'styles/components/orderDetailModal';
import { useTranslation } from 'react-i18next';

export default ({
  shoppingCart,
  openMessageModal,
  handleProductsWithoutStock,
  openShoppingModal,
  setOpenShoppingModal,
  generalColor,
}: OrderDetailsModalProps): JSX.Element => {
  const mediumScreen = useMediumScreen();
  const { storeNotProcessedOrder } = useNotProcessedOrders();
  const { currentVenue, isConnected } = React.useContext(OrderContext);
  let message: string;
  const formattedTotalAmount = useFormatMoneyAuto(calculateTotalAmount(shoppingCart.detail));
  const { t } = useTranslation();

  const saveOrder = async (actualShoppingCart: ShoppingCart): Promise<void | null> => {
    if (!currentVenue || !isConnected) return null;
    const productsWithoutStock = await checkProductsStock(actualShoppingCart.detail);
    if (productsWithoutStock.length > 0) {
      handleProductsWithoutStock(productsWithoutStock, false);
    } else {
      const order = await storeFoodTruckOrder(
        actualShoppingCart,
        currentVenue.id,
        currentVenue.localDateTimeIdentifier,
      );
      if (order) {
        message = `${t('order')} ${order.id}, ${t('payment-registered-success')}`;
        openMessageModal(message, true, isConnected);
      } else {
        message = t('payment-registered-error');
        openMessageModal(message, false, isConnected);
      }
    }
  };

  const saveInStorage = async (actualShoppingCart: ShoppingCart): Promise<void> => {
    await storeNotProcessedOrder(actualShoppingCart);
    message = t('saveInStorage-error');
    openMessageModal(message, false, isConnected);
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={openShoppingModal}
      onRequestClose={() => setOpenShoppingModal(false)}
    >
      <View style={mediumScreen ? styles.orderProductsLarge : styles.orderProductsSmall}>
        <View style={styles.returnContainer}>
          <TouchableOpacity onPress={() => setOpenShoppingModal(false)}>
            <View style={styles.closeContainer}>
              <Text style={[styles.goToPaymentText, { color: generalColor }]}>{t('back')}</Text>
              <Icon
                name={'cross'}
                type="entypo"
                size={mediumScreen ? 40 : 35}
                style={mediumScreen ? styles.iconLarge : styles.iconSmall}
                color={generalColor}
                containerStyle={styles.containerIcon}
              />
            </View>
          </TouchableOpacity>
        </View>
        <ScrollView style={styles.productsContainer} contentContainerStyle={styles.productContainerScrollContent}>
          <View style={mediumScreen ? styles.modalProductsContainerLarge : styles.modalProductsContainerSmall}>
            <Text style={mediumScreen ? styles.sectionContainerTitleLarge : styles.sectionContainerTitleSmall}>
              {t('order-summary')}
            </Text>
            <View style={mediumScreen ? styles.productsContainerLarge : styles.productsContainerSmall}>
              {shoppingCart.detail.map((detail) => {
                return (
                  <View key={detail.product.id} style={[mediumScreen ? styles.rowLarge : styles.rowSmall]}>
                    <Text style={styles.itemTextProduct}>{`${detail.product.name} ${
                      detail.quantity > 1 ? `(${detail.quantity})` : ''
                    }`}</Text>
                    <Text style={styles.itemTextProduct}>{`$${detail.product.price * detail.quantity}`}</Text>
                  </View>
                );
              })}
            </View>
          </View>
          <View style={styles.totalAmountContainer}>
            <Text
              style={[
                mediumScreen ? styles.totalAmountTextLarge : styles.totalAmountTextSmall,
                { color: currentVenue?.appColor ?? '#001C47' },
              ]}
            >{`${t('total')}: ${formattedTotalAmount}`}</Text>
          </View>
        </ScrollView>
        <View style={mediumScreen ? styles.paymentButtonDownContainerLarge : styles.paymentButtonDownContainerSmall}>
          <Button
            title={t('proccess-payment')}
            onPress={async () => {
              setOpenShoppingModal(false);
              try {
                const order = await saveOrder(shoppingCart);
                if (order === null) {
                  throw new Error();
                }
              } catch (error) {
                console.log(error);
                await saveInStorage(shoppingCart);
              }
            }}
            containerStyle={mediumScreen ? styles.paymentButtonContainerLarge : styles.paymentButtonContainerSmall}
            buttonStyle={{ backgroundColor: generalColor }}
            titleStyle={styles.paymentButtonTitle}
          />
        </View>
      </View>
    </Modal>
  );
};
